// ============================================
// NRS.05 — Intake Modal Orchestration Service
// ============================================
// Spec: hlx-nrs-05-intake-modal-orchestration.md (LOCKED)
// Contract: Marker detection, modal rendering, submit/dismiss tracking,
//           flag persistence, duplicate suppression, sex-gating
// ============================================

import type {
  ModalType,
  ModalState,
  ModalSubmission,
  ModalOrchestratorState,
  IntakeModalMetrics,
} from '../types/intakeModalOrchestration';
import {
  MARKER_TO_MODAL,
  MODAL_SEQUENCE_FEMALE,
  MODAL_SEQUENCE_MALE,
} from '../types/intakeModalOrchestration';
import type { DataCompletenessFlags } from '../types/triageSession';
import { INITIAL_DATA_COMPLETENESS_FLAGS } from '../types/triageSession';

// ── Metrics State ──

let _metrics: IntakeModalMetrics = {
  totalCalls: 0,
  successRate: 1,
  avgLatencyMs: 0,
  errorsByType: {},
  accuracyOnGoldStandard: 0,
};
let _totalLatency = 0;
let _successCount = 0;

// ── Orchestrator ──

export class ModalOrchestratorService {
  private state: ModalOrchestratorState;

  constructor(patientSex: 'female' | 'male' | undefined) {
    this.state = {
      modals: {
        goals: createInitialModalState('goals'),
        medications: createInitialModalState('medications'),
        allergies: createInitialModalState('allergies'),
        pregnancy: createInitialModalState('pregnancy'),
      },
      dataCompletenessFlags: { ...INITIAL_DATA_COMPLETENESS_FLAGS },
      patientSex: patientSex,
      allModalsPresented: false,
    };
    // B1: Male patients skip pregnancy modal — auto-set pregnancyStatusProvided
    if (patientSex === 'male') {
      this.state.dataCompletenessFlags.pregnancyStatusProvided = true;
    }
    // B2: undefined sex = conservative default (show pregnancy modal, same as female)
  }

  /**
   * Detect if a marker string maps to a modal type.
   * Returns the modal type or null if not a modal marker.
   */
  detectModal(marker: string): ModalType | null {
    return MARKER_TO_MODAL[marker] || null;
  }

  /**
   * Check if a modal should be shown.
   * Handles sex-gating (pregnancy modal females only) and duplicate suppression.
   */
  shouldShowModal(modalType: ModalType): boolean {
    // Sex-gating: pregnancy modal only for females and undefined (conservative default)
    if (modalType === 'pregnancy' && this.state.patientSex === 'male') {
      return false;
    }
    // undefined sex = show pregnancy modal (conservative safety posture)

    const modal = this.state.modals[modalType];

    // Duplicate suppression: don't show if already submitted
    if (modal.status === 'submitted') {
      return false;
    }

    return true;
  }

  /**
   * Mark a modal as shown. Increments show count for tracking.
   */
  markModalShown(modalType: ModalType): void {
    const modal = this.state.modals[modalType];
    modal.status = 'shown';
    modal.shownAt = new Date();
    modal.showCount++;
  }

  /**
   * Process a modal submission. Updates DataCompletenessFlags.
   */
  processSubmission(submission: ModalSubmission): DataCompletenessFlags {
    const startTime = Date.now();
    _metrics.totalCalls++;

    const modal = this.state.modals[submission.modalType];

    if (submission.action === 'submit') {
      modal.status = 'submitted';
      modal.completedAt = submission.submittedAt;
      this.updateCompletionFlags(submission.modalType, true);
    } else {
      // Dismiss — mark as dismissed but do NOT flip flag
      modal.status = 'dismissed';
      modal.completedAt = submission.submittedAt;
      // DataCompletenessFlags remain false for dismissed modals
    }

    this.checkAllModalsPresented();

    _successCount++;
    _totalLatency += Date.now() - startTime;
    return { ...this.state.dataCompletenessFlags };
  }

  /**
   * Get the expected modal sequence for this patient.
   */
  getModalSequence(): ModalType[] {
    // undefined sex uses female sequence (conservative — includes pregnancy modal)
    return this.state.patientSex === 'male'
      ? [...MODAL_SEQUENCE_MALE]
      : [...MODAL_SEQUENCE_FEMALE];
  }

  /**
   * Get the next modal to show (based on sequence and completion status).
   */
  getNextModal(): ModalType | null {
    const sequence = this.getModalSequence();
    for (const modalType of sequence) {
      const modal = this.state.modals[modalType];
      if (modal.status === 'pending') {
        return modalType;
      }
    }
    return null;
  }

  /**
   * Get current state (immutable copy).
   */
  getState(): Readonly<ModalOrchestratorState> {
    return {
      ...this.state,
      modals: {
        goals: { ...this.state.modals.goals },
        medications: { ...this.state.modals.medications },
        allergies: { ...this.state.modals.allergies },
        pregnancy: { ...this.state.modals.pregnancy },
      },
      dataCompletenessFlags: { ...this.state.dataCompletenessFlags },
    };
  }

  /**
   * Get current DataCompletenessFlags.
   */
  getDataCompletenessFlags(): DataCompletenessFlags {
    return { ...this.state.dataCompletenessFlags };
  }

  /**
   * Check if all required modals have been submitted.
   */
  isIntakeComplete(): boolean {
    return this.state.allModalsPresented;
  }

  // ── Private Methods ──

  private updateCompletionFlags(modalType: ModalType, submitted: boolean): void {
    // Only flip flags on submit, never on dismiss (LOCKED contract)
    if (!submitted) return;

    switch (modalType) {
      case 'medications':
        this.state.dataCompletenessFlags.medicationsProvided = true;
        break;
      case 'allergies':
        this.state.dataCompletenessFlags.allergiesProvided = true;
        break;
      case 'pregnancy':
        this.state.dataCompletenessFlags.pregnancyStatusProvided = true;
        break;
      case 'goals':
        // Goals don't map to a DataCompletenessFlag
        break;
    }
  }

  private checkAllModalsPresented(): void {
    const sequence = this.getModalSequence();
    this.state.allModalsPresented = sequence.every((modalType) => {
      const modal = this.state.modals[modalType];
      return modal.status === 'submitted' || modal.status === 'dismissed';
    });
  }
}

// ── Factory ──

export function createModalOrchestrator(patientSex: 'female' | 'male' | undefined): ModalOrchestratorService {
  return new ModalOrchestratorService(patientSex);
}

// ── Helpers ──

function createInitialModalState(type: ModalType): ModalState {
  return {
    type,
    status: 'pending',
    shownAt: null,
    completedAt: null,
    showCount: 0,
  };
}

// ── Metrics ──

export function getMetrics(): IntakeModalMetrics {
  return {
    ..._metrics,
    successRate: _metrics.totalCalls > 0 ? _successCount / _metrics.totalCalls : 1,
    avgLatencyMs: _metrics.totalCalls > 0 ? _totalLatency / _metrics.totalCalls : 0,
  };
}
